// @ts-nocheck

import React, { useMemo } from 'react';
import styled from 'styled-components';

import theme from '../../styled-components/dark';
import { CardModes, type CardMode } from '../../types/common';
import type { OutcomeStatus, VoidReason } from '../../types/history';
import type { SportId } from '../../types/sports';
import HistoryItemOutcomeCardContent from '../HistoryItemOutcomeCardContent';
import HistoryItemOutcomeCardMatch from '../HistoryItemOutcomeCardMatch';
import HistoryItemOutcomeCardProgressBarContainer from '../HistoryItemOutcomeCardProgressBar/HistoryItemOutcomeCardProgressBarContainer';
import Link from '../Link';
import ScoreboardMultimatchContainer from '../ScoreboardMultimatch/ScoreboardMultimatchContainer';

const Wrapper = styled(Link)`
  border-bottom: 1px solid ${theme.History.outcome.borderColor};
  color: ${theme.History.outcome.color};
  padding: 0.67em;
  width: 100%;
`;

const BetResult = styled.div`
  color: ${theme.History.outcome.betResult.color};
  background: ${theme.History.outcome.betResult.backgroundColor};
  border-radius: 0.95em;

  font-size: 0.7em;
  padding: 0.53em 0.73em;
  margin-top: 0.48em;
`;

const StyledScoreboardMultimatchContainer = styled(ScoreboardMultimatchContainer)`
  justify-content: left;
  padding-top: 0.4em;
  font-size: 0.77em;

  & > .team {
    color: white;
  }
`;

type HistoryItemOutcomeCardProps = {
  startDate: number | null;
  sportId: SportId;
  categoryId: number;
  flagId?: string;
  isLive?: boolean;
  isEnded?: boolean;
  cardBadgeHasAttach?: boolean;
  className?: string;
  status: OutcomeStatus;
  outcome: unknown;
  title: string;
  betResult: string | null;
  odds: number;
  initialOdds: number | null;
  cardMode: CardMode;
  to?: string;
  isBanker?: boolean;
  isOutright: boolean;
  competitor1?: string;
  flag1?: string;
  competitor2?: string;
  flag2?: string;
  matchId?: string;
  isMyMatch?: boolean;
  voidReason?: VoidReason;
  isSettled?: boolean;
  isSimpleBet?: boolean;
  isUserPrefsCompact?: boolean;
};

const HistoryItemOutcomeCard = ({
  startDate,
  sportId,
  categoryId,
  flagId,
  isLive = false,
  isEnded = false,
  status,
  cardMode,
  title,
  betResult,
  odds,
  initialOdds,
  to,
  outcome,
  className,
  isBanker,
  isOutright,
  competitor1,
  flag1,
  competitor2,
  flag2,
  matchId,
  isMyMatch = false,
  voidReason,
  isSettled,
  isSimpleBet,
  isUserPrefsCompact,
}: HistoryItemOutcomeCardProps) => {
  const isBoosted = useMemo(
    () => cardMode === CardModes.BOOSTED || cardMode === CardModes.BIG_BOOSTED || cardMode === CardModes.XMAS_BOOSTED,
    [cardMode],
  );

  const shouldDisplayFlags = useMemo(
    () => (isOutright ? !!outcome?.flag : !!competitor1 && !!flag1 && !!competitor2 && !!flag2),
    [isOutright, outcome, competitor1, flag1, competitor2, flag2],
  );

  const shouldDisplayBetResult = useMemo(() => !!betResult && !voidReason, [betResult, voidReason]);

  return (
    <Wrapper className={className} to={to}>
      <HistoryItemOutcomeCardContent
        initialOdds={initialOdds}
        odds={odds}
        isBanker={isBanker}
        outcome={outcome}
        flagId={flagId}
        shouldDisplayFlag={shouldDisplayFlags}
        isMyMatch={isMyMatch}
        status={status}
        isSimpleBet={isSimpleBet}
        isUserPrefsCompact={isUserPrefsCompact}
        shouldDisplayOutcomeContent
      />
      <HistoryItemOutcomeCardProgressBarContainer />
      <HistoryItemOutcomeCardMatch
        sportId={sportId}
        categoryId={categoryId}
        flagId={flagId}
        matchId={matchId}
        competitor1={competitor1}
        competitor2={competitor2}
        flag1={flag1}
        flag2={flag2}
        isLive={isLive}
        isEnded={isEnded}
        title={title}
        startDate={startDate}
        isSettled={isSettled}
        voidReason={voidReason}
        isBoosted={isBoosted}
        cardMode={cardMode}
      />
      <StyledScoreboardMultimatchContainer matchId={matchId} />
      {shouldDisplayBetResult ? <BetResult dangerouslySetInnerHTML={{ __html: betResult }} /> : null}
    </Wrapper>
  );
};

export default HistoryItemOutcomeCard;
